import React, { useState } from 'react';
import parseISO from 'date-fns/parseISO';

import useEvent from '@lyearn/core/hooks/useEvent';
import { DateFormat, formatDateWithYear } from '@lyearn/core/utils/date/formatDate';
import Form from '@lyearn/form/src/Form';
import { TFunction, Trans, useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import { Fields, FieldType } from '@/components/Form';
import { Modal } from '@/components/Modals';
import { EventHandlerComponentProps } from '@/hooks/useEventHandler/types';
import { EventStatusEnum, EventUnion } from '@/types/schema';
import { getUtcToZonedTime } from '@/utils/timezone';

import useEventUpdate from '../../../EventDetails/hooks/useEventUpdate';
import { renderSessionDateRangePicker } from '../../../EventEditor/components/Editor/components/FormDateRangePicker';
import { validateSessionDate } from '../../../EventEditor/components/Editor/config';
import { EventFormInput } from '../../../EventEditor/types';
import { getResolvedTimezone, getResolvedType } from '../../../EventEditor/utils';

type RescheduleSessionFormInput = Partial<EventFormInput>;

const getFields = (t: TFunction, status: EventStatusEnum): Fields<RescheduleSessionFormInput> => ({
  date: {
    name: 'date',
    type: FieldType.SlowCustom,
    Component: renderSessionDateRangePicker({ t, status, direction: 'col' }),
  },
});

const getInitialValues = (event: EventUnion): RescheduleSessionFormInput => {
  const { timezone, startTime, endTime, type } = event;

  const resolvedTimezone = getResolvedTimezone(timezone);

  return {
    timezone: resolvedTimezone,
    startTime: getUtcToZonedTime(resolvedTimezone, startTime),
    endTime: getUtcToZonedTime(resolvedTimezone, endTime),
    type: getResolvedType(type),
  };
};

const validateForm = (values: RescheduleSessionFormInput) => {
  const errors: any = {};
  const dateError = validateSessionDate(values);
  if (dateError) {
    errors.date = dateError;
  }
  return errors;
};

export type RescheduleSessionModalProps = {
  event: EventUnion;
};

const RescheduleSessionModal: React.FC<
  RescheduleSessionModalProps & EventHandlerComponentProps
> = ({ onClose, event }) => {
  const { t } = useTranslation();
  const [formikSubmit, setFormikSubmit] = useState<() => void>(() => {});
  const fields = getFields(t, event.status);
  const { fetching, onUpdateEvent } = useEventUpdate({ eventId: event.id });
  const { showSnackbar } = useSnackbar();

  const onSubmit = useEvent((values: RescheduleSessionFormInput) => {
    const { startTime, endTime } = values;
    onUpdateEvent({ startTime, endTime }, () => {
      showSnackbar({
        message: (
          <Trans
            i18nKey="Events:RecurringSession.RescheduleSessionSuccess"
            values={{ date: formatDateWithYear(parseISO(values?.startTime!), DateFormat.DayDate) }}
          />
        ),
      });
      onClose();
    });
  });

  const initialValues = getInitialValues(event);

  return (
    <Modal
      action={{
        text: t('Common:Actions.Reschedule'),
        onSubmit: formikSubmit,
        isSubmitting: fetching,
      }}
      className="px-24 pt-8"
      header={{
        title: t('Events:RecurringSession.RescheduleSession'),
        description: (
          <Trans
            i18nKey="Events:RecurringSession.RescheduleSessionDescription"
            values={{ name: event.name }}
            variant="label-01"
          />
        ),
      }}
      onClose={onClose}>
      <Form<RescheduleSessionFormInput>
        fields={fields}
        formName="Reschedule Session Modal"
        initialValues={initialValues}
        setSubmitRef={setFormikSubmit}
        validate={validateForm}
        onSubmit={onSubmit}
      />
    </Modal>
  );
};

export default RescheduleSessionModal;
